import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Autocomplete, Chip, TextField, Typography} from "@mui/material";
import {Loader} from "@googlemaps/js-api-loader";
import {enqueueSnackbar} from "notistack";
import {PageTitle, RecordPage} from "../../shared/components/record/RecordKit";
import {CenteredCircularSpinner} from "../../shared/components/CenteredCircularSpinner";
import {logError} from "../../shared/services/logger.service";
import {SavaquanHttpError} from "../../shared/utils/http-error";
import {formatAddressLine, getLocationFromAddress} from "../../shared/misc/location-helpers";
import {getPricingRules, savePricingRules} from "../../shared/services/pricing-rules.service";
import {CompanyPricingRulesDto} from "../../shared/dtos/company-pricing-rules.dto";
import {getCrewCandidates} from "../job/services/crew-member.service";
import {CrewCandidateDto} from "../job/dtos/company-crew-member.dto";
import {
  addedTotal,
  applyJobPricing,
  increasePercentAt,
  JobPricingRules,
  jobPricingTotal,
  MAX_INCREASE_PERCENT,
} from "../../shared/pricing/job-pricing";

/*
 * SQ-283: the Job Pricing settings page — a port of the prototype
 * (JobPricingMockUp.jsx): label on the left, control on the right, a short
 * note per section, and a graph carrying a live marker tied to the test job
 * in Test a job. Everything is editable at all times; nothing commits until
 * Save changes; Test a job runs on the PENDING values. The math is the shared
 * JobPricing module (the same as the server's and Android's).
 */

const GREEN = "#4caf50";
const GREEN_DEEP = "#2e7d32"; // SQ-538: the iOS app's button green
const DEEP = "#2f7a33";
const INK = "#1e221f";
const MUTED = "#6f7772";
const HAIR = "#e2e6e3";
const PAPER = "#ffffff";
const WASH = "#f3faf3";
const BAND = "#dcefdc";
const EDGE = "#d8ebd9";
const LAV = "#f9f7fd";
const LAVEDGE = "#e3d9f0";

const DEFAULT_SPAN = 4;
const MAX_HOURS = 12;
const MAX_MILES = 200;
const MAX_ADDRESS_SUGGESTIONS = 10;
const SAVED_FLASH_MS = 1600;

/** The form as edited: money and percent as typed strings, hours and miles as numbers. */
interface RulesForm {
  minimumJobCharge: string;
  startingIncreasePercent: string;
  returnHours: number;
  shopAddressLine: string;
  freeMiles: number;
  perMileRate: string;
}

const formFromDto = (dto: CompanyPricingRulesDto): RulesForm => ({
  minimumJobCharge: dto.minimumJobCharge > 0 ? String(dto.minimumJobCharge) : "",
  startingIncreasePercent: dto.startingIncreasePercent > 0 ? String(dto.startingIncreasePercent) : "",
  returnHours: Math.floor((dto.returnMinutes ?? 0) / 60),
  shopAddressLine: formatAddressLine(dto.shopAddress, dto.shopCity, dto.shopState, dto.shopPostalCode),
  freeMiles: dto.freeMiles ?? 0,
  perMileRate: dto.perMileRate > 0 ? String(dto.perMileRate) : "",
});

const sameForm = (a: RulesForm, b: RulesForm) =>
  a.minimumJobCharge === b.minimumJobCharge &&
  a.startingIncreasePercent === b.startingIncreasePercent &&
  a.returnHours === b.returnHours &&
  a.shopAddressLine === b.shopAddressLine &&
  a.freeMiles === b.freeMiles &&
  a.perMileRate === b.perMileRate;

const rulesOf = (form: RulesForm): JobPricingRules => ({
  minimumJobCharge: parseFloat(form.minimumJobCharge) || 0,
  startingIncreasePercent: Math.min(MAX_INCREASE_PERCENT, parseFloat(form.startingIncreasePercent) || 0),
  returnMinutes: form.returnHours * 60,
  freeMiles: form.freeMiles,
  perMileRate: parseFloat(form.perMileRate) || 0,
});

function scaleFor(pct: number) {
  const ceiling = Math.max(100, Math.ceil(pct / 100) * 100);
  const step = [20, 50, 100].find((n) => ceiling / n <= 12) ?? 100;
  return { ceiling, marks: Array.from({ length: ceiling / step + 1 }, (_, i) => i * step) };
}

function fmtShort(t: number) {
  const h = Math.floor(t + 0.0001);
  const m = Math.round((t - h) * 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function money(n: number) {
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function perHour(n: number) {
  const v = n % 1 === 0 ? n.toLocaleString("en-US") : n.toFixed(2);
  return `$${v}/hr`;
}

const initialsOf = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("");

const shortName = (name: string) => {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return name;
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

export const JobPricingPage: React.FC = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [savedDto, setSavedDto] = useState<CompanyPricingRulesDto | null>(null);
  const [saved, setSaved] = useState<RulesForm | null>(null);
  const [pending, setPending] = useState<RulesForm | null>(null);
  const [candidates, setCandidates] = useState<CrewCandidateDto[]>([]);

  // Test a job (nothing here saves)
  const [onJob, setOnJob] = useState<number[]>([]);
  const [jobH, setJobH] = useState(0);
  const [jobM, setJobM] = useState(0);
  const [jobMiles, setJobMiles] = useState(0);
  const [equipCost, setEquipCost] = useState("");
  const [addlCost, setAddlCost] = useState("");

  // Places autocomplete for the shop address (the client page's pattern)
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null);
  const [addressSuggestions, setAddressSuggestions] = useState<string[]>([]);

  useEffect(() => {
    // The prototype's faces (DM Serif Display, DM Sans), loaded once from
    // Google Fonts so the page matches it exactly; the styles below name
    // them with Georgia / system-ui fallbacks.
    if (!document.getElementById('job-pricing-fonts')) {
      const link = document.createElement('link');
      link.id = 'job-pricing-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@400;500;700&display=swap';
      document.head.appendChild(link);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getPricingRules(navigate)
      .then((dto) => {
        const form = formFromDto(dto);
        setSavedDto(dto);
        setSaved(form);
        setPending(form);
      })
      .catch((e) => {
        if (e instanceof SavaquanHttpError && e.statusCode === 403) {
          setForbidden(true);
          return;
        }
        logError('Error loading pricing rules', {}, e);
        enqueueSnackbar(`Error loading pricing rules: ${e}`, { variant: 'error', autoHideDuration: 5000 });
      })
      .finally(() => setIsLoading(false));
    getCrewCandidates(navigate)
      .then(setCandidates)
      .catch((e) => logError('Error loading crew candidates', {}, e));

    const googleMapsLoader = new Loader({
      apiKey: "AIzaSyBLw50vYSozTEviDIpu94K1KuwM4X_hDUM",
      version: "beta",
    });
    Promise.all([googleMapsLoader.importLibrary("places"), googleMapsLoader.importLibrary("maps")])
      .then(([places, maps]) => {
        setAutocompleteService(new (places as google.maps.PlacesLibrary).AutocompleteService());
        const mapDiv = document.createElement('div');
        const map = new (maps as google.maps.MapsLibrary).Map(mapDiv, {});
        setPlacesService(new (places as google.maps.PlacesLibrary).PlacesService(map));
      })
      .catch((e) => logError('Error importing google places library', {}, e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDirty = !!saved && !!pending && !sameForm(saved, pending);

  // Warn on leaving with unsaved changes (the browser's own prompt).
  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const addressLine = pending?.shopAddressLine ?? "";
  useEffect(() => {
    if (!autocompleteService || !placesService || !addressLine || !isDirty) return;
    autocompleteService.getPlacePredictions({
      input: addressLine,
      componentRestrictions: { country: 'us' },
      locationBias: "IP_BIAS",
      region: 'US',
    })
      .then((response) => {
        const predictionPromises = response.predictions.slice(0, MAX_ADDRESS_SUGGESTIONS).map((prediction) =>
          new Promise<string>((resolve) => {
            placesService.getDetails({
              placeId: prediction.place_id,
              fields: ['address_components', 'formatted_address'],
            }, (place, status) => {
              if (status === google.maps.places.PlacesServiceStatus.OK && place) {
                let formattedAddress = place.formatted_address || prediction.description;
                if (formattedAddress.slice(-5) === ', USA') formattedAddress = formattedAddress.slice(0, -5);
                resolve(formattedAddress);
              } else {
                resolve(prediction.description);
              }
            });
          }));
        Promise.all(predictionPromises)
          .then(setAddressSuggestions)
          .catch(() => setAddressSuggestions(response.predictions.map((p) => p.description).slice(0, MAX_ADDRESS_SUGGESTIONS)));
      })
      .catch((e) => logError('Error getting address suggestions', { address: addressLine }, e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressLine, placesService]);

  const edit = useCallback((patch: Partial<RulesForm>) => {
    setPending((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const flashTimer = useRef<number | null>(null);
  useEffect(() => () => { if (flashTimer.current) window.clearTimeout(flashTimer.current); }, []);

  const save = async () => {
    if (!pending || !savedDto || saving) return;
    setSaving(true);
    try {
      let shop = {
        shopAddress: savedDto.shopAddress, shopCity: savedDto.shopCity, shopState: savedDto.shopState,
        shopPostalCode: savedDto.shopPostalCode, shopLat: savedDto.shopLat, shopLng: savedDto.shopLng,
      };
      const trimmedLine = pending.shopAddressLine.trim();
      if (!trimmedLine) {
        shop = { shopAddress: null, shopCity: null, shopState: null, shopPostalCode: null, shopLat: null, shopLng: null };
      } else if (trimmedLine !== (saved?.shopAddressLine ?? "")) {
        // The box edits the whole line; the geocoder splits it (SQ-502).
        const location = await getLocationFromAddress(trimmedLine);
        shop = {
          shopAddress: location.street, shopCity: location.city, shopState: location.state,
          shopPostalCode: location.postalCode, shopLat: location.lat, shopLng: location.lng,
        };
      }
      const rules = rulesOf(pending);
      const dto = await savePricingRules(navigate, { ...rules, ...shop });
      const form = formFromDto(dto);
      setSavedDto(dto);
      setSaved(form);
      setPending(form);
      setSavedFlash(true);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setSavedFlash(false), SAVED_FLASH_MS);
    } catch (e) {
      logError('Error saving pricing rules', {}, e instanceof Error ? e : undefined);
      enqueueSnackbar(`Error saving pricing rules: ${e instanceof Error ? e.message : e}`, { variant: 'error', autoHideDuration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  // ---- derived values (the prototype's, through the shared math) ----
  const form = pending;
  const rules = useMemo(() => (form ? rulesOf(form) : null), [form]);
  const pct = rules?.startingIncreasePercent ?? 0;
  const { ceiling, marks: pctMarks } = scaleFor(pct);
  const endT = form?.returnHours ?? 0;
  const hasPct = pct > 0;
  const hasEnd = endT > 0;
  const curveReady = hasPct && hasEnd;

  const premiumAt = useCallback((t: number) => (rules ? increasePercentAt(rules, t * 60) : 0), [rules]);

  const picked = candidates.filter((c) => onJob.includes(c.companyCrewMemberId));
  const crew = picked.length;
  const rateSum = picked.reduce((t, c) => t + (c.billableHourlyRate ?? 0), 0);
  const rate = crew > 0 ? rateSum / crew : 0;

  const jobT = jobH + jobM / 60;
  const wallMinutes = jobH * 60 + jobM;
  const crewHours = jobT * crew;
  const labor = jobT * rateSum;
  const equipment = parseFloat(equipCost) || 0;
  const additional = parseFloat(addlCost) || 0;
  const input = {
    laborCost: labor, laborMinutes: wallMinutes * crew, crewCount: crew,
    equipmentCost: equipment, costItemsTotal: additional, travelMiles: jobMiles,
  };
  const result = applyJobPricing(input, rules);
  const increaseAmt = result.smallJobRateAmount;
  const minTopUp = result.minimumTopUpAmount;
  const travelCharge = result.travelAmount;
  const minApplied = minTopUp > 0;
  const finalPrice = jobPricingTotal(input, result);

  // What the labor hour earns once the rules have had their say. Equipment
  // and cost items stay out of it: they are not labor.
  const laborSide = labor + addedTotal(result);
  const effRate = crewHours > 0 ? laborSide / crewHours : 0;
  const rateBump = effRate - rate;
  const many = crew > 1;
  const rateFrom = many ? rateSum : rate;
  const rateTo = many ? (jobT > 0 ? laborSide / jobT : 0) : effRate;
  const causes: string[] = [];
  if (increaseAmt > 0) causes.push("the small job rate");
  if (minApplied) causes.push("the job minimum");
  if (travelCharge > 0) causes.push("the travel charge");

  // ---- chart ----
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxW, setBoxW] = useState(600);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => setBoxW(Math.max(240, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [isLoading, forbidden]);

  const axisMax = hasEnd ? Math.max(1, Math.ceil(endT)) : DEFAULT_SPAN;
  const hourMarks = Array.from({ length: axisMax + 1 }, (_, i) => i);
  const W = boxW;
  const padL = 34, padR = 16;
  const plotW = W - padL - padR;
  const spacing = plotW / axisMax;

  const labelW = (l: string) => Math.max(28, l.length * 6.6 + 8);
  const labelAt = (p: number, tier: number) => {
    if (p <= 0) return tier === 0 ? "standard" : "std";
    const n = p.toFixed(0);
    return tier === 0 ? `+${n}% more` : tier === 1 ? `+${n}%` : `${n}%`;
  };
  const widest = (t: number) => Math.max(...hourMarks.map((h) => labelW(labelAt(premiumAt(h), t))));
  const tier = [0, 1].find((t) => widest(t) <= spacing - 6) ?? 2;

  const padT = 54;
  const padB = 46;
  const plotH = 170;
  const H = padT + plotH + padB;
  const xAt = (t: number) => padL + (Math.min(t, axisMax) / axisMax) * plotW;
  const yAt = (p: number) => padT + (1 - p / ceiling) * plotH;

  const showMarker = curveReady && jobT > 0;
  const markerPct = premiumAt(Math.min(jobT, axisMax));
  const markerRate = rateSum * (1 + markerPct / 100);

  if (isLoading) return <RecordPage name="JobPricingPage"><CenteredCircularSpinner /></RecordPage>;
  if (forbidden) {
    return (
      <RecordPage name="JobPricingPage">
        <Typography variant="body1" textAlign="center" color="text.secondary">
          Only company owners and admins can change job pricing.
        </Typography>
      </RecordPage>
    );
  }
  if (!form) return null;

  return (
    <RecordPage name="JobPricingPage">
      <div style={S.page} className="jp-page">
        <style>{`
          .jp-page * { box-sizing: border-box; }
          .jp-page input:focus, .jp-page button:focus-visible { outline: 2px solid ${DEEP}; outline-offset: 1px; }
          .jp-page .rk-page-title { margin-bottom: 0; }
          .jp-page .rk-page-title-text {
            font-family: 'DM Serif Display', Georgia, serif;
            font-size: 30px;
            line-height: 1.15;
            font-weight: 400;
          }
        `}</style>

        <PageTitle title="Job Pricing">
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            {isDirty && <Chip label="Unsaved" size="small" color="warning" variant="outlined" />}
            <button
              type="button"
              style={{ ...S.save, ...(savedFlash ? S.savedBtn : null), opacity: saving ? 0.7 : 1 }}
              disabled={saving}
              onClick={() => void save()}
            >
              {savedFlash ? "Saved" : "Save changes"}
            </button>
          </div>
        </PageTitle>
        <p style={S.sub}>Stop losing money. Set the rules your job pricing runs on.</p>

        <section style={S.card}>
          <div style={S.band}>
            <h2 style={S.h2}>Small job pricing</h2>
          </div>
          <div style={S.body}>
            <div style={S.stack}>
              <span style={S.stackLabel}>Minimum job charge</span>
              <MoneyStepper value={form.minimumJobCharge} onChange={(v) => edit({ minimumJobCharge: v })} label="Minimum job charge" />
            </div>

            <div style={S.rule} />

            <p style={S.lede}>
              Increase your hourly rate by a set percentage for smaller jobs, then set the hour it
              returns to your normal hourly rates.
            </p>

            <div style={S.chartBox} ref={boxRef}>
              <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: "block" }} role="img" aria-label="Rate increase by job time">
                <rect x={padL} y={padT} width={plotW} height={plotH} fill={PAPER} stroke={EDGE} />

                {pctMarks.map((p) => (
                  <g key={`p${p}`}>
                    {p > 0 && p < ceiling && (
                      <line x1={padL} x2={padL + plotW} y1={yAt(p)} y2={yAt(p)} stroke={HAIR} />
                    )}
                    <text x={padL - 6} y={yAt(p) + 3.5} textAnchor="end" style={S.scaleText}>
                      {p}%
                    </text>
                  </g>
                ))}

                {hourMarks.map((h) => (
                  <g key={h}>
                    <line x1={xAt(h)} x2={xAt(h)} y1={padT} y2={padT + plotH} stroke={h === 0 ? "#c9cfcb" : HAIR} />
                    <text x={xAt(h)} y={padT + plotH + 18} textAnchor="middle" style={S.axisText}>
                      {h}h
                    </text>
                  </g>
                ))}
                <text x={padL + plotW / 2} y={padT + plotH + 38} textAnchor="middle" style={S.scaleText}>
                  JOB TIME
                </text>

                <circle cx={xAt(0)} cy={yAt(pct)} r="6" fill={PAPER} stroke={hasPct ? GREEN : "#c9cfcb"} strokeWidth="3" />

                {hasPct && !hasEnd && (
                  <line x1={xAt(0)} x2={xAt(axisMax)} y1={yAt(pct)} y2={yAt(pct)} stroke={GREEN} strokeWidth="3" strokeDasharray="6 6" opacity="0.5" />
                )}

                {curveReady && (
                  <>
                    <polyline points={`${xAt(0)},${yAt(pct)} ${xAt(endT)},${yAt(0)}`} fill="none" stroke={GREEN} strokeWidth="3" />
                    <circle cx={xAt(0)} cy={yAt(pct)} r="6" fill={PAPER} stroke={GREEN} strokeWidth="3" />
                    <circle cx={xAt(endT)} cy={yAt(0)} r="6" fill={PAPER} stroke={GREEN} strokeWidth="3" />
                    {hourMarks.map((h) => {
                      const p = premiumAt(h);
                      const l = labelAt(p, tier);
                      const cx = Math.min(Math.max(xAt(h), 4 + labelW(l) / 2), W - 4 - labelW(l) / 2);
                      return (
                        <g key={`r${h}`}>
                          <text x={cx} y={yAt(p) - 13} textAnchor="middle" style={S.readout}>
                            {l}
                          </text>
                          <circle cx={xAt(h)} cy={yAt(p)} r="4" fill={GREEN} />
                        </g>
                      );
                    })}
                  </>
                )}

                {hasEnd && !hasPct && (
                  <circle cx={xAt(endT)} cy={yAt(0)} r="6" fill={PAPER} stroke={GREEN} strokeWidth="3" />
                )}

                {showMarker && (
                  <MarkerCallout
                    x={xAt(jobT)}
                    y={yAt(markerPct)}
                    top={16}
                    width={W}
                    lines={[
                      `${fmtShort(jobT)} at ${perHour(markerRate)}`,
                      crew > 1 ? `whole crew, +${markerPct.toFixed(1)}%` : `+${markerPct.toFixed(1)}%`,
                    ]}
                  />
                )}
              </svg>
            </div>

            <div style={S.stackRow}>
              <div style={S.stack}>
                <span style={S.stackLabel}>Starting increase</span>
                <PctStepper value={form.startingIncreasePercent} onChange={(v) => edit({ startingIncreasePercent: v })} max={MAX_INCREASE_PERCENT} />
              </div>
              <div style={S.stack}>
                <span style={S.stackLabel}>Normal rates at</span>
                <UnitStepper label="Normal rates at" value={form.returnHours} onChange={(v) => edit({ returnHours: v })} unit="HR" max={MAX_HOURS} />
              </div>
            </div>
          </div>
        </section>

        <section style={S.card}>
          <div style={S.band}>
            <h2 style={S.h2}>Travel</h2>
          </div>
          <div style={S.body}>
            <p style={S.lede}>Add an extra charge for jobs further out.</p>

            <div style={{ ...S.stack, width: "100%", maxWidth: 380 }}>
              <span style={S.stackLabel}>Shop or yard address</span>
              <Autocomplete
                disablePortal
                freeSolo
                fullWidth
                filterOptions={(o) => o}
                options={addressSuggestions}
                inputValue={form.shopAddressLine}
                onInputChange={(_, value) => edit({ shopAddressLine: value })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Start typing an address"
                    inputProps={{ ...params.inputProps, 'aria-label': 'Shop or yard address' }}
                  />
                )}
                sx={{ background: PAPER, marginBottom: '6px' }}
              />
            </div>

            <div style={S.stackRow}>
              <div style={S.stack}>
                <span style={S.stackLabel}>No charge within</span>
                <UnitStepper label="No charge within" value={form.freeMiles} onChange={(v) => edit({ freeMiles: v })} unit="MI" max={MAX_MILES} />
              </div>
              <div style={S.stack}>
                <span style={S.stackLabel}>Per mile after that</span>
                <MoneyStepper value={form.perMileRate} onChange={(v) => edit({ perMileRate: v })} label="Per mile after that" />
              </div>
            </div>

            <p style={S.travelNote}>
              Charged one way, for each mile past your free radius, on top of the job price.
            </p>
          </div>
        </section>

        <section style={S.checkCard}>
          <div style={S.checkBand}>
            <h2 style={S.h2}>Test a job</h2>
          </div>
          <div style={S.body}>
            <p style={S.groupNote}>
              Test an example job to see the settings above in action. Nothing here saves.
            </p>

            <div style={S.crewLabelRow}>
              <span style={S.rowLabel}>Who is on the job</span>
              {crew > 0 && <span style={S.crewSummary}>{crew} crew at {perHour(rateSum)}</span>}
            </div>
            <CrewPicker
              roster={candidates}
              selected={onJob}
              onToggle={(id) =>
                setOnJob(onJob.includes(id) ? onJob.filter((x) => x !== id) : [...onJob, id])
              }
            />

            <div style={{ ...S.stackRow, marginTop: 20 }}>
              <TimeStepper
                label="Job duration"
                hours={jobH}
                minutes={jobM}
                onChange={(h, m) => { setJobH(h); setJobM(m); }}
              />
              <div style={S.stack}>
                <span style={S.stackLabel}>Equipment charge</span>
                <MoneyStepper value={equipCost} onChange={setEquipCost} label="Equipment charge" />
              </div>
              <div style={S.stack}>
                <span style={S.stackLabel}>Additional cost items</span>
                <MoneyStepper value={addlCost} onChange={setAddlCost} label="Additional cost items" />
              </div>
              <div style={S.stack}>
                <span style={S.stackLabel}>Miles from the shop</span>
                <UnitStepper label="Miles from the shop" value={jobMiles} onChange={setJobMiles} unit="MI" max={MAX_MILES} />
              </div>
            </div>

            <div style={S.receipt} data-testid="test-a-job-receipt">
              <table style={S.receiptTable}>
                <tbody>
                  {equipment > 0 && <Line label="Total Equipment Cost" value={money(equipment)} />}
                  <Line
                    label="Total Labor Time & Cost"
                    qual={jobT > 0 ? fmtShort(jobT) : ""}
                    value={money(labor)}
                  />
                  {additional > 0 && (
                    <Line label="Total Additional Cost Items" value={money(additional)} />
                  )}
                  {increaseAmt > 0 && (
                    <Line label="Added for small job rate" value={money(increaseAmt)} />
                  )}
                  {minApplied && <Line label="Added to meet job minimum" value={money(minTopUp)} />}
                  {travelCharge > 0 && <Line label="Added for travel" value={money(travelCharge)} />}
                  <tr>
                    <td style={{ ...S.totalCell, ...S.totalLabel }}>Job Total</td>
                    <td style={{ ...S.totalCell, ...S.totalValue, textAlign: "right" }} colSpan={2}>{money(finalPrice)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {rateBump > 0.005 && (
              <div style={S.rateBox} data-testid="rate-sentence">
                <div style={S.rateSentence}>
                  {many ? "Combined hourly" : "Hourly"} rate increased from normal{" "}
                  <span style={S.rateFrom}>{money(rateFrom)}</span> to{" "}
                  <span style={S.rateTo}>{money(rateTo)}</span>
                </div>
                <div style={S.rateNote}>Due to {causes.join(" and ")}</div>
              </div>
            )}
          </div>
        </section>
      </div>
    </RecordPage>
  );
};

export default JobPricingPage;

/* ---------- pieces ---------- */

/** Callout pinned to the top of the chart with a leader down to the curve. */
function MarkerCallout({ x, y, top, width, lines }: { x: number; y: number; top: number; width: number; lines: string[] }) {
  const w = Math.max(...lines.map((l) => l.length)) * 6.4 + 20;
  const h = lines.length * 15 + 10;
  const bx = Math.min(Math.max(x - w / 2, 4), width - 4 - w);
  return (
    <g>
      <line x1={x} y1={top + h} x2={x} y2={y - 5} stroke={DEEP} strokeWidth="1.25" strokeDasharray="3 3" />
      <rect x={bx} y={top} width={w} height={h} rx="6" fill="#eef6ee" stroke={DEEP} strokeWidth="1.25" />
      {lines.map((l, i) => (
        <text key={i} x={bx + w / 2} y={top + 17 + i * 15} textAnchor="middle" style={S.callout}>
          {l}
        </text>
      ))}
      <circle cx={x} cy={y} r="5.5" fill={DEEP} stroke={PAPER} strokeWidth="2" />
    </g>
  );
}

const digitsW = (v: string | number) => Math.max(22, String(v || "0").length * 11);

/** One rounded box split in three: minus, value, plus. The buttons fill the full height. */
function Seg({ onLess, onMore, children, lessLabel, moreLabel }: {
  onLess: () => void; onMore: () => void; children: React.ReactNode; lessLabel: string; moreLabel: string;
}) {
  return (
    <span style={S.seg}>
      <Round label={lessLabel} onStep={onLess} side="left">&minus;</Round>
      <span style={S.segValue}>{children}</span>
      <Round label={moreLabel} onStep={onMore} side="right">+</Round>
    </span>
  );
}

function UnitStepper({ label, value, onChange, unit, max = 99, min = 0 }: {
  label: string; value: number; onChange: (v: number) => void; unit: string; max?: number; min?: number;
}) {
  return (
    <Seg
      lessLabel={`Less ${label}`}
      moreLabel={`More ${label}`}
      onLess={() => onChange(Math.max(min, value - 1))}
      onMore={() => onChange(Math.min(max, value + 1))}
    >
      <span aria-label={`${label} value`} style={{ display: "inline-flex", alignItems: "baseline", gap: 3 }}>
        <span style={S.segNum}>{value}</span>
        <span style={S.segUnit}>{unit}</span>
      </span>
    </Seg>
  );
}

function MoneyStepper({ value, onChange, label, max = 99999 }: {
  value: string; onChange: (v: string) => void; label: string; max?: number;
}) {
  const step = (d: number) =>
    onChange(String(Math.min(max, Math.max(0, Math.round((parseFloat(value) || 0) + d)))));
  return (
    <Seg lessLabel={`Less ${label}`} moreLabel={`More ${label}`} onLess={() => step(-1)} onMore={() => step(1)}>
      <span style={S.segUnit}>$</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, "").slice(0, 6))}
        placeholder="0"
        inputMode="decimal"
        aria-label={label}
        style={{ ...S.segInput, width: digitsW(value) }}
      />
    </Seg>
  );
}

function PctStepper({ value, onChange, max }: { value: string; onChange: (v: string) => void; max: number }) {
  const step = (d: number) =>
    onChange(String(Math.min(max, Math.max(0, Math.round((parseFloat(value) || 0) + d)))));
  return (
    <Seg lessLabel="Less Starting increase" moreLabel="More Starting increase" onLess={() => step(-1)} onMore={() => step(1)}>
      <input
        value={value}
        onChange={(e) => {
          const v = e.target.value.replace(/[^0-9.]/g, "").slice(0, 5);
          const n = parseFloat(v);
          // A half-typed "." or "." alone is not a number yet, never "over the cap".
          onChange(v === "" || Number.isNaN(n) || n <= max ? v : String(max));
        }}
        placeholder="0"
        inputMode="decimal"
        aria-label="Starting increase"
        style={{ ...S.segInput, width: digitsW(value) }}
      />
      <span style={S.segUnit}>%</span>
    </Seg>
  );
}

/** Label and live readout share a line, the pill sits under them. */
function TimeStepper({ hours, minutes, onChange, label, maxHours = MAX_HOURS }: {
  hours: number; minutes: number; onChange: (h: number, m: number) => void; label: string; maxHours?: number;
}) {
  const bumpM = (d: number) => {
    let m = minutes + d;
    if (m > 59) m = 0;
    if (m < 0) m = 59;
    onChange(hours, m);
  };
  const readout =
    hours === 0 && minutes === 0
      ? ""
      : hours === 0
      ? `${minutes}M`
      : minutes === 0
      ? `${hours}H`
      : `${hours}H ${minutes}M`;

  return (
    <div style={S.stack}>
      <div style={S.labelLine}>
        <span style={S.stackLabel}>{label}</span>
        <span style={S.timeReadout} aria-label="Job duration value">{readout}</span>
      </div>
      <span style={S.seg}>
        <Round label="Subtract an hour" onStep={() => onChange(Math.max(0, hours - 1), minutes)} side="left">&minus;</Round>
        <span style={S.timeUnit}>HR</span>
        <Round label="Add an hour" onStep={() => onChange(Math.min(maxHours, hours + 1), minutes)} side="right">+</Round>
        <span style={S.segSplit} />
        <Round label="Subtract a minute" onStep={() => bumpM(-1)} side="left">&minus;</Round>
        <span style={S.timeUnit}>MIN</span>
        <Round label="Add a minute" onStep={() => bumpM(1)} side="right">+</Round>
      </span>
    </div>
  );
}

/** Press-and-hold repeats and accelerates; a single tap moves exactly one. */
function Round({ children, onStep, label, side }: { children: React.ReactNode; onStep: () => void; label: string; side: "left" | "right" }) {
  const stepRef = useRef(onStep);
  const timer = useRef<number | null>(null);
  const delay = useRef(420);
  useEffect(() => { stepRef.current = onStep; });
  const stop = () => {
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
  };
  useEffect(() => stop, []);
  const start = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const active = typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null;
    if (active && active !== e.currentTarget && typeof active.blur === "function") active.blur();
    stop();
    stepRef.current();
    delay.current = 420;
    const tick = () => {
      stepRef.current();
      delay.current = Math.max(28, delay.current * 0.7);
      timer.current = window.setTimeout(tick, delay.current);
    };
    timer.current = window.setTimeout(tick, delay.current);
  };
  return (
    <button
      type="button"
      aria-label={label}
      onPointerDown={start}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); stepRef.current(); }
      }}
      style={{ ...S.segBtn, ...(side === "left" ? S.segLeft : S.segRight) }}
    >
      {children}
    </button>
  );
}

function CrewPicker({ roster, selected, onToggle }: { roster: CrewCandidateDto[]; selected: number[]; onToggle: (id: number) => void }) {
  const [open, setOpen] = useState(false);
  const picked = roster.filter((c) => selected.includes(c.companyCrewMemberId));

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label="Who is on the job"
        style={S.crewField}
      >
        <span style={S.crewFieldInner}>
          {picked.length > 0 ? (
            picked.map((c) => (
              <span key={c.companyCrewMemberId} style={S.miniAvatar}>
                {initialsOf(c.name)}
              </span>
            ))
          ) : (
            <span style={S.crewEmpty}>Select crew members</span>
          )}
        </span>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d={open ? "M6 15l6-6 6 6" : "M6 9l6 6 6-6"}
            stroke={GREEN}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div style={S.crewGrid}>
          {roster.length === 0 && <span style={S.crewName}>No team members with a billable rate yet.</span>}
          {roster.map((c) => {
            const on = selected.includes(c.companyCrewMemberId);
            // A member without a billable rate cannot price a job (SQ-522).
            const selectable = c.selectable && c.billableHourlyRate != null;
            return (
              <button
                key={c.companyCrewMemberId}
                type="button"
                aria-label={`Crew ${c.name}`}
                aria-pressed={on}
                disabled={!selectable}
                onClick={() => onToggle(c.companyCrewMemberId)}
                style={{ ...S.crewCell, opacity: selectable ? 1 : 0.4, cursor: selectable ? "pointer" : "default" }}
              >
                <span style={{ ...S.avatar, ...(on ? S.avatarOn : S.avatarOff) }}>{initialsOf(c.name)}</span>
                <span style={S.crewName}>{shortName(c.name)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Line({ label, value, qual }: { label: string; value: string; qual?: string }) {
  return (
    <tr>
      <td style={{ ...S.lineCell, ...S.lineLabel }}>{label}</td>
      <td style={{ ...S.lineCell, ...S.lineQual }}>{qual ?? ""}</td>
      <td style={{ ...S.lineCell, ...S.lineValue }}>{value}</td>
    </tr>
  );
}

/* ---------- styles (the prototype's) ---------- */

const S: Record<string, React.CSSProperties> = {
  // The column already gives the page its 16px side padding (RecordKit's
  // .rk-body); only the top/bottom rhythm is this page's own.
  page: {
    background: PAPER,
    padding: "28px 0 64px",
    fontFamily: "'DM Sans', system-ui, sans-serif",
    color: INK,
  },
  sub: { margin: "8px 0 22px", color: MUTED, fontSize: 15, lineHeight: 1.5 },
  save: {
    flexShrink: 0,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 15,
    fontWeight: 700,
    color: PAPER,
    background: GREEN_DEEP,
    border: "none",
    borderRadius: 999,
    padding: "10px 24px",
    cursor: "pointer",
  },
  savedBtn: { background: DEEP },

  card: { background: WASH, border: `1px solid ${EDGE}`, borderRadius: 12, marginBottom: 16, overflow: "hidden" },
  band: { background: BAND, borderBottom: `1px solid ${EDGE}`, padding: "12px 16px" },
  checkCard: { background: LAV, border: `1px solid ${LAVEDGE}`, borderRadius: 12, marginBottom: 16, overflow: "hidden" },
  checkBand: { background: "#ede7f6", borderBottom: `1px solid ${LAVEDGE}`, padding: "12px 16px" },
  h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 22, margin: 0, fontWeight: 400 },
  body: { padding: 16 },
  rule: { height: 1, background: EDGE, margin: "16px 0" },

  rowLabel: { fontSize: 15, fontWeight: 500, color: INK },
  stackRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    gap: "14px 12px",
    margin: "4px 0 2px",
  },
  stack: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, minWidth: 0 },
  stackLabel: { fontSize: 13.5, fontWeight: 500, color: MUTED },

  travelNote: { margin: "12px 0 0", fontSize: 13.5, color: MUTED, lineHeight: 1.5, maxWidth: 520 },
  lede: { margin: "0 0 14px", fontSize: 15, color: INK, lineHeight: 1.5 },
  groupNote: { margin: "0 0 12px", fontSize: 13.5, color: MUTED, lineHeight: 1.5, maxWidth: 520 },

  chartBox: { border: `1px solid ${EDGE}`, borderRadius: 8, padding: 8, marginBottom: 12, background: PAPER, overflow: "hidden" },
  axisText: { fontFamily: "'DM Sans', sans-serif", fontSize: 12, fill: MUTED },
  scaleText: { fontFamily: "'DM Sans', sans-serif", fontSize: 9.5, fill: "#a7afa9" },
  readout: {
    fontFamily: "'DM Sans', sans-serif", fontSize: 12.5, fontWeight: 700, fill: INK,
    paintOrder: "stroke", stroke: PAPER, strokeWidth: 3, strokeLinejoin: "round",
  },
  callout: { fontFamily: "'DM Sans', sans-serif", fontSize: 11.5, fontWeight: 700, fill: INK },

  seg: {
    display: "inline-flex",
    alignItems: "stretch",
    height: 42,
    border: `1.5px solid ${INK}`,
    borderRadius: 999,
    background: PAPER,
    overflow: "hidden",
  },
  segSplit: { width: 1.5, background: INK, flexShrink: 0 },
  labelLine: { display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "2px 10px", minHeight: 19 },
  timeReadout: { fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, color: INK },
  timeUnit: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 34,
    padding: "0 6px",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.05em",
    color: MUTED,
  },
  segValue: {
    display: "inline-flex",
    alignItems: "baseline",
    justifyContent: "center",
    gap: 3,
    minWidth: 36,
    padding: "0 6px",
    alignSelf: "center",
    flexShrink: 0,
  },
  segNum: { fontFamily: "'DM Sans', sans-serif", fontSize: 17, fontWeight: 700, color: INK },
  segUnit: { fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", color: MUTED },
  segInput: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 17,
    fontWeight: 700,
    color: INK,
    textAlign: "center",
    border: "none",
    outline: "none",
    background: "transparent",
    padding: 0,
    flexShrink: 0,
  },
  segBtn: {
    width: 44,
    flexShrink: 0,
    border: "none",
    background: "#f4f7f4",
    color: INK,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 19,
    lineHeight: 1,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    touchAction: "none",
    userSelect: "none",
    WebkitUserSelect: "none",
    WebkitTapHighlightColor: "transparent",
  },
  segLeft: { borderRight: `1.5px solid ${INK}` },
  segRight: { borderLeft: `1.5px solid ${INK}` },

  crewLabelRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 10 },
  crewSummary: { fontSize: 13, color: MUTED, fontWeight: 500 },
  crewField: {
    width: "100%",
    maxWidth: 420,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    minHeight: 54,
    padding: "8px 12px",
    border: `2px solid ${GREEN}`,
    borderRadius: 12,
    background: PAPER,
    cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
  },
  crewFieldInner: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 },
  crewEmpty: { fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: MUTED },
  miniAvatar: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: GREEN,
    color: PAPER,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    fontWeight: 700,
  },
  crewGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(60px, 1fr))", gap: 10,
    border: `1px solid ${LAVEDGE}`, borderRadius: 10, background: PAPER, padding: 14, marginTop: 10,
  },
  crewCell: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
    border: "none", background: "transparent", padding: 0, cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
  },
  avatar: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 48, height: 48, borderRadius: "50%",
    fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 700,
  },
  avatarOn: { background: GREEN, color: PAPER },
  avatarOff: { background: "#e2e2e2", color: "#7d7d7d" },
  crewName: { fontFamily: "'DM Sans', sans-serif", fontSize: 12.5, color: MUTED, textAlign: "center" },

  receipt: { marginTop: 16, background: PAPER, border: `1px solid ${LAVEDGE}`, borderRadius: 10, padding: "4px 14px 14px" },
  receiptTable: { width: "100%", borderCollapse: "collapse" },
  lineCell: { padding: "8px 0", borderBottom: `1px solid ${LAVEDGE}`, verticalAlign: "baseline" },
  lineLabel: { fontSize: 15, color: "#33383a", whiteSpace: "nowrap", textAlign: "left" },
  lineQual: { fontSize: 14, color: "#33383a", whiteSpace: "nowrap", paddingRight: 12, textAlign: "right", width: "1%" },
  lineValue: { fontSize: 15, fontWeight: 500, color: INK, whiteSpace: "nowrap", textAlign: "right", width: "1%" },
  totalCell: { paddingTop: 14, verticalAlign: "baseline" },
  totalLabel: { fontSize: 19, fontWeight: 700 },
  totalValue: { fontSize: 25, fontWeight: 700, color: INK },

  rateBox: { marginTop: 14, border: `1px solid ${LAVEDGE}`, borderRadius: 8, padding: "12px 14px", background: PAPER },
  rateSentence: { fontSize: 15, lineHeight: 1.5, color: INK },
  rateFrom: { fontWeight: 700, whiteSpace: "nowrap" },
  rateTo: { fontWeight: 700, color: DEEP, whiteSpace: "nowrap" },
  rateNote: { marginTop: 8, fontSize: 13, color: MUTED, lineHeight: 1.4 },
};
